import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import BaseLayout from '@/shared/components/accounts/AccountBase';
import { GET } from '@/shared/api/requests';

const ConfirmEmailPage = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasConfirmed = useRef(false);

  useEffect(() => {
    const confirmEmail = async () => {
      if (!key) {
        setStatus('error');
        setMessage('Invalid confirmation link');
        return;
      }

      // Prevent duplicate API calls (React StrictMode runs useEffect twice in dev)
      if (hasConfirmed.current) {
        console.log('Email confirmation already in progress, skipping duplicate call');
        return;
      }
      hasConfirmed.current = true;

      console.log('=== EMAIL CONFIRMATION DEBUG ===');
      console.log('Key from URL:', key);
      console.log('Calling API:', `/users/auth/confirm-email/${key}/`);

      try {
        const response = await GET(`/users/auth/confirm-email/${key}/`);
        console.log('API Response:', response);
        
        setStatus('success');
        setMessage(response.data.message || 'Email confirmed successfully!');
      } catch (error: any) {
        console.error('API Error:', error);
        console.error('Error status:', error?.status);
        console.error('Error data:', error?.data);
        
        setStatus('error');
        setMessage(
          error?.data?.detail || 
          error?.data?.error || 
          'Failed to confirm email. The link may be invalid or expired.'
        );
      }
    };

    confirmEmail();
  }, [key, navigate]);

  return (
    <BaseLayout>
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">Email Confirmation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Confirming your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <Alert className="bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold">{message}</span>
                  </div>
                </AlertDescription>
              </Alert>
              <div className="text-center space-y-3">
                <p className="text-sm text-gray-700">
                  Your account is now verified and ready to use!
                </p>
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full"
                  size="lg"
                >
                  Continue to Login
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  {message}
                </AlertDescription>
              </Alert>
              <div className="flex flex-col space-y-2">
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Go to Login
                </Button>
                <Button 
                  onClick={() => navigate('/signup')}
                  variant="outline"
                  className="w-full"
                >
                  Sign Up Again
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </BaseLayout>
  );
};

export default ConfirmEmailPage;
